import { useEffect, useRef } from "react";

/** Il thread di bolle di una conversazione, in un posto solo.
 *
 * Lo stesso pattern viveva copiato inline in due punti: il transcript del
 * dettaglio conversazione (`ConversazioneDetail.tsx`) e la card di sola
 * lettura di `Review3.tsx`. Questa estrazione parte dalla PRIMA: la variante
 * di `Review3` e' nata per un riquadro di riepilogo (altezza fissa 240px,
 * nessuno scroll automatico, nessuna intestazione di turno) e non regge una
 * finestra in cui i messaggi si aggiungono mentre la guardi.
 *
 * Due cose la rendono usabile per il dialogo dal vivo e non solo per la
 * rilettura:
 *  - `key` sull'`id` del messaggio, non sull'indice: quando arriva un turno
 *    nuovo React aggiorna in coda invece di rimontare tutte le bolle (e con
 *    l'indice ogni append avrebbe fatto ri-renderizzare, e perdere lo stato
 *    del DOM, di tutta la lista);
 *  - `autoScroll`: il turno appena arrivato deve essere visibile senza che
 *    l'utente trascini la barra, altrimenti la risposta che stai misurando
 *    e' proprio quella che non vedi.
 */

/** Sottoinsieme di `ConversazioneTranscriptMessage` di cui il thread ha
 *  bisogno: si accetta la forma minima, non il tipo dell'API, perche' il
 *  componente non deve sapere da quale endpoint arrivano i messaggi. */
export interface ConversationThreadMessage {
  id: string;
  direction: string;
  normalized_text: string | null;
  processing_status?: string;
  turn_seq?: number | null;
  attachments?: unknown[];
  received_at?: string | null;
  created_at?: string | null;
}

/** Porta il contenitore in fondo. Esportata perche' e' l'unica parte del
 *  comportamento di scroll che si possa provare senza un browser vero: in
 *  jsdom l'altezza di layout e' sempre 0, quindi un test sul componente
 *  montato non distinguerebbe "sono in fondo" da "non ho fatto nulla". */
export function scrollToBottom(el: HTMLElement | null): void {
  if (!el) return;
  el.scrollTop = el.scrollHeight;
}

export interface ConversationThreadProps {
  messages: ConversationThreadMessage[];
  /** Altezza massima prima dello scroll. Il default e' quello del dettaglio. */
  maxHeight?: string;
  /** Segue i messaggi nuovi. Acceso nella finestra di prova, spento nella
   *  rilettura di una conversazione chiusa (dove saltare in fondo
   *  nasconderebbe l'inizio). */
  autoScroll?: boolean;
  /** Mostra turno, orario e `processing_status` sopra ogni bolla. E' la parte
   *  diagnostica: `processing_status` dice se il messaggio e' stato davvero
   *  elaborato o e' rimasto `received`/`failed`. */
  showMeta?: boolean;
  emptyLabel?: string;
  /** Formattazione dell'orario: la decide il chiamante, che conosce la
   *  convenzione della sua pagina. */
  formatTimestamp?: (iso: string | null) => string;
  /** Reso sotto la bolla — usato dalla finestra di prova per attaccare al
   *  turno la sua causa (reason code, gate, stato del draft). */
  renderMessageFooter?: (m: ConversationThreadMessage) => React.ReactNode;
  /** Inverte lato E colore rispetto al default (inbound=sinistra/grigio,
   *  outbound=destra/blu): usato dalla pagina di chat `ProvaAgenteChat`, dove
   *  chi guarda RECITA il cliente — la propria bolla (inbound) deve stare a
   *  destra ed essere quella evidenziata, come in una chat vera, e l'agente
   *  (outbound) a sinistra. Il default resta invariato per `ConversazioneDetail`
   *  e `Review3.tsx`, dove si rilegge una conversazione dall'esterno e non si
   *  e' nessuna delle due parti. */
  flipAlignment?: boolean;
}

export function ConversationThread({
  messages,
  maxHeight = "34rem",
  autoScroll = false,
  showMeta = true,
  emptyLabel = "Nessun messaggio",
  formatTimestamp,
  renderMessageFooter,
  flipAlignment = false,
}: ConversationThreadProps) {
  const boxRef = useRef<HTMLDivElement>(null);

  // Dipende dal NUMERO di messaggi, non dall'array: rileggere il thread dopo
  // un turno produce comunque un array nuovo (`fetchConversazioneDetail`
  // restituisce oggetti nuovi), e agganciarsi al riferimento farebbe
  // ri-scrollare a ogni ricarica anche quando non e' arrivato nulla.
  useEffect(() => {
    if (autoScroll) scrollToBottom(boxRef.current);
  }, [messages.length, autoScroll]);

  if (messages.length === 0) {
    return <span className="text-muted text-sm">{emptyLabel}</span>;
  }

  return (
    <div
      ref={boxRef}
      data-testid="conversation-thread"
      style={{
        display: "flex", flexDirection: "column", gap: "var(--space-2)",
        maxHeight, overflowY: "auto",
      }}
    >
      {messages.map((m) => {
        const inbound = m.direction === "inbound";
        // Di default il lato/colore segue `inbound`; capovolto, segue
        // l'opposto — vedi la nota su `flipAlignment` nell'interfaccia.
        const onLeft = flipAlignment ? !inbound : inbound;
        const allegati = m.attachments?.length ?? 0;
        return (
          <div
            key={m.id}
            data-testid={`bubble-${m.direction}`}
            style={{
              alignSelf: onLeft ? "flex-start" : "flex-end",
              maxWidth: "85%",
              background: onLeft
                ? "var(--indaco-gray-50)"
                : "var(--indaco-brand-primary-light)",
              borderRadius: "var(--border-radius-md)",
              padding: "var(--space-2) var(--space-3)",
            }}
          >
            {showMeta && (
              <div
                className="text-xs text-muted"
                style={{ marginBottom: "var(--space-1)", display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}
              >
                <span>{inbound ? "Utente" : "Agente"}</span>
                {m.turn_seq != null && <span>turno {m.turn_seq}</span>}
                {formatTimestamp && <span>{formatTimestamp(m.received_at ?? m.created_at ?? null)}</span>}
                {m.processing_status && (
                  <span className="badge badge-neutral" style={{ fontSize: "var(--font-size-xs)" }}>
                    {m.processing_status}
                  </span>
                )}
              </div>
            )}
            <div className="text-sm" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {m.normalized_text ?? <span className="text-muted">(nessun testo)</span>}
            </div>
            {allegati > 0 && (
              <div className="text-xs text-muted" style={{ marginTop: "var(--space-1)" }}>
                {allegati} allegat{allegati === 1 ? "o" : "i"}
              </div>
            )}
            {renderMessageFooter?.(m)}
          </div>
        );
      })}
    </div>
  );
}
